import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router';
import { deletePageParamIfSearchUpdated } from './useFilter';

const validateNumberString = (value: string | null, fallback = 0): number => {
  const parsed = Number.parseInt(value || '', 10);

  return Number.isNaN(parsed) ? fallback : parsed;
};

export const parsePaginationParams = (
  params: URLSearchParams,
  initialValue: PaginationState = { page: 1, pageSize: 10 },
): PaginationState => {
  const pageSize = validateNumberString(params.get('pageSize'), initialValue.pageSize);
  const page = validateNumberString(params.get('page'), initialValue.page);

  return { pageSize, page };
};

const useRoutingPaginationParams = (initialValue: PaginationState) => {
  const [params, setSearchParams] = useSearchParams();

  const { pageSize, page } = parsePaginationParams(params, initialValue);

  const handleSetParams = (newState: PaginationState) => {
    if (newState.page !== initialValue.page) {
      params.set('page', `${newState.page}`);
    } else {
      params.delete('page');
    }
    if (newState.pageSize !== initialValue.pageSize) {
      params.set('pageSize', `${newState.pageSize}`);
    } else {
      params.delete('pageSize');
    }
    deletePageParamIfSearchUpdated(newState, params);
    setSearchParams(params, { replace: true });
  };

  return { paramsData: { page, pageSize }, handleSetParams };
};

interface PaginationState {
  page: number;
  pageSize: number;
}

export const useLocalPagination = <T>(unpaginatedData: T[]) => {
  const { paramsData, handleSetParams } = useRoutingPaginationParams({ pageSize: 10, page: 1 });

  const { page, pageSize } = paramsData;
  const startOffset = (page - 1) * pageSize;
  const endOffset = startOffset + pageSize;

  const totalRecords = unpaginatedData.length;

  const data = unpaginatedData.slice(startOffset, endOffset);

  const onPagination = useCallback(
    (page: number, pageSize: number) => {
      handleSetParams({
        page,
        pageSize,
      });
    },
    [handleSetParams],
  );

  const goToPage = useCallback(
    (page: number) => {
      handleSetParams({
        page,
        pageSize: paramsData.pageSize,
      });
    },
    [paramsData.pageSize, handleSetParams],
  );

  const totalPages = Math.ceil(totalRecords / pageSize);

  return { data, totalRecords, totalPages, onPagination, goToPage, ...paramsData };
};

export function usePaginationState(initialState: PaginationState = { page: 1, pageSize: 10 }) {
  const [page, setPage] = useState(initialState.page);
  const [pageSize, setPageSize] = useState(initialState.pageSize);

  return { page, pageSize, setPage, setPageSize };
}
